/**
 * The BidNova card imaging pipeline — deliberately simple.
 *
 * decode (EXIF orientation) → optional seller crop / 90° rotation →
 * Lanczos derivative ladder.
 *
 * Nothing else touches the pixels: no background removal, no studio scene,
 * no pedestal, no tone or colour changes. The published image is exactly the
 * photo the seller uploaded, after the edits they made themselves.
 *
 * Runs in a Web Worker when the platform supports OffscreenCanvas, and falls
 * back to the main thread otherwise. The RAW upload is preserved byte-for-byte.
 */

import { encodeCanvas, makeCanvas, ctx2d, supportsType, type AnyCanvas } from "./canvas";
import { decodeImageFile } from "./decode";
import { CONFIDENCE_GATE, detectCardQuad, padQuad, rectifyCard, type Quad } from "./geometry";
import { imgLog } from "./diagnostics";
import { highQualityDownscale } from "./resize";
import { SIZE_DEEPZOOM, SIZE_PREVIEW, SIZE_THUMB, SIZE_ULTRA } from "./constants";

export type StudioStage =
  | "decoding"
  | "detecting"
  | "rectifying"
  | "encoding"
  | "done";

export type StudioDerivatives = {
  /** Highest quality rendition — the deep-zoom source. */
  deepzoom: Blob;
  /** Ultra HD listing image. */
  ultra: Blob;
  /** Gallery image. */
  preview: Blob;
  /** Grid thumbnail. */
  thumb: Blob;
  /** Mime type used for the derivatives (webp when supported). */
  mime: string;
  ext: string;
};

export type StudioResult = {
  derivatives: StudioDerivatives;
  meta: {
    sourceWidth: number;
    sourceHeight: number;
    wasHeic: boolean;
    /** EXIF orientation applied while decoding (1 = none). */
    orientation: number;
    /** Card edges were found in the photo (informational only). */
    quadDetected: boolean;
    coverage: number;
    /** 0–1 detection confidence (1 when the seller cropped manually). */
    confidence: number;
    /** True when the seller should be offered the manual crop editor. */
    needsManualCrop: boolean;
    /** Whether the published render used a seller-supplied crop. */
    manualCrop: boolean;
    /** Whether the seller applied a 90° rotation. */
    rotated: boolean;
    frameWidth: number;
    frameHeight: number;
  };
};

export type StudioOptions = {
  /**
   * Seller-supplied crop from the manual editor, in normalised (0–1)
   * coordinates of the upright decoded photo: TL, TR, BR, BL.
   */
  quad?: Quad;
  /** Extra rotation applied by the manual editor, in 90° steps. */
  rotateQuarters?: number;
};

async function pickMime(): Promise<{ mime: string; ext: string }> {
  if (await supportsType("image/webp")) return { mime: "image/webp", ext: "webp" };
  return { mime: "image/jpeg", ext: "jpg" };
}

function fitWithin(w: number, h: number, longEdge: number) {
  const k = Math.min(1, longEdge / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/** Aspect sanity for anything we claim is a card. Slabs are the widest at ~0.66. */
function aspectPlausible(c: AnyCanvas): boolean {
  const w = (c as HTMLCanvasElement).width;
  const h = (c as HTMLCanvasElement).height;
  if (!w || !h) return false;
  const ratio = Math.min(w, h) / Math.max(w, h);
  return ratio >= 0.3 && ratio <= 1;
}

/** Rotate a canvas by whole quarter turns (manual editor). */
function rotateCanvas(src: AnyCanvas, quarters: number): AnyCanvas {
  const q = ((quarters % 4) + 4) % 4;
  if (q === 0) return src;
  const w = (src as HTMLCanvasElement).width;
  const h = (src as HTMLCanvasElement).height;
  const swap = q % 2 === 1;
  const out = makeCanvas(swap ? h : w, swap ? w : h);
  const c = ctx2d(out);
  c.translate((swap ? h : w) / 2, (swap ? w : h) / 2);
  c.rotate((q * Math.PI) / 2);
  c.drawImage(src as CanvasImageSource, -w / 2, -h / 2);
  return out;
}

export async function processCardImage(
  file: File,
  options: StudioOptions = {},
  onStage?: (stage: StudioStage) => void,
): Promise<StudioResult> {
  const { quad: manualQuad, rotateQuarters = 0 } = options;

  onStage?.("decoding");
  const decoded = await decodeImageFile(file);

  onStage?.("detecting");
  let card: AnyCanvas = rotateQuarters ? rotateCanvas(decoded.canvas, rotateQuarters) : decoded.canvas;
  const base = card;
  const rotated = ((rotateQuarters % 4) + 4) % 4 !== 0;
  let quadDetected = false;
  let coverage = 0;
  let confidence = 0;
  let manualCrop = false;

  if (manualQuad) {
    // The seller drew this crop, so it is trusted unconditionally — padded
    // outward a hair so the border they traced is fully inside the render.
    const bw = (base as HTMLCanvasElement).width;
    const bh = (base as HTMLCanvasElement).height;
    const px = padQuad(
      manualQuad.map((p) => ({ x: p.x * bw, y: p.y * bh })) as Quad,
      0.01,
      { w: bw, h: bh },
    );
    onStage?.("rectifying");
    const rect = rectifyCard(base, px, { preserveOrientation: true });
    if (aspectPlausible(rect.canvas)) {
      card = rect.canvas;
      quadDetected = true;
      manualCrop = true;
      confidence = 1;
    }
  } else {
    // Detection is informational only: it decides whether to nudge the seller
    // towards the crop editor. The uploaded pixels are never auto-cropped.
    try {
      const detection = detectCardQuad(base);
      coverage = detection.coverage;
      confidence = detection.score;
      quadDetected = detection.confident;
    } catch {
      /* detection is advisory */
    }
  }

  const width = (card as HTMLCanvasElement).width;
  const height = (card as HTMLCanvasElement).height;
  imgLog("pipeline", "Prepared image", { width, height, manualCrop, rotateQuarters });

  onStage?.("encoding");
  const { mime, ext } = await pickMime();
  const master = fitWithin(width, height, SIZE_DEEPZOOM);
  const ultraSize = fitWithin(width, height, SIZE_ULTRA);
  const previewSize = fitWithin(width, height, SIZE_PREVIEW);
  const thumbSize = fitWithin(width, height, SIZE_THUMB);

  const deepzoom = await encodeCanvas(
    master.w === width && master.h === height ? card : highQualityDownscale(card, master.w, master.h),
    mime,
    0.95,
  );
  const ultra = await encodeCanvas(
    highQualityDownscale(card, ultraSize.w, ultraSize.h),
    mime,
    0.93,
  );
  const preview = await encodeCanvas(
    highQualityDownscale(card, previewSize.w, previewSize.h),
    mime,
    0.9,
  );
  const thumb = await encodeCanvas(
    highQualityDownscale(card, thumbSize.w, thumbSize.h),
    mime,
    0.84,
  );

  onStage?.("done");
  return {
    derivatives: { deepzoom, ultra, preview, thumb, mime, ext },
    meta: {
      sourceWidth: decoded.sourceWidth,
      sourceHeight: decoded.sourceHeight,
      wasHeic: decoded.wasHeic,
      orientation: decoded.orientation,
      quadDetected,
      coverage,
      confidence,
      needsManualCrop: !manualCrop && confidence < CONFIDENCE_GATE,
      manualCrop,
      rotated,
      frameWidth: width,
      frameHeight: height,
    },
  };
}
